// 플랜별 권한/제한을 조회하기 위한 공통 명세 (user-plan 전용)
// - planId로 바로 조회 가능하도록 양방향 매핑을 유지합니다.
// - flag / limit / option 세 영역으로 나눠 확장성을 확보합니다.

// 사용자 행동별 코인 차감 값을 정의
export const FEATURE_USAGE_COIN_COST = {
  /** Insight - 키워드 검색 (서버 API 사용 시) */
  insightKeywordSearch: 5,
  /** Insight - 영상 상세 조회 (서버 API 사용 시) */
  insightVideoDetail: 5,
  /** Insight - 채널 분석 (서버 API 사용 시) */
  insightChannelAnalysis: 5,
  /** Insight - AI 영상 구조 분석 */
  insightAiVideoAnalysis: 10,
  /** Insight - AI 댓글 분석 */
  insightAiCommentAnalysis: 10,
  /** Insight - AI 썸네일 분석 */
  insightAiThumbnailAnalysis: 10,
  /** Insight - AI 제목 분석 */
  insightAiTitleAnalysis: 10,
  /** Insight - AI 영상 비교 분석 */
  insightAiVideoComparison: 30,
  /** Insight - AI 키워드 번역 */
  insightAiKeywordTranslation: 2,
  /** Insight - AI 키워드 추천 */
  insightAiKeywordRecommend: 5,
  /** Insight - AI Shorts 성과 예측 */
  insightAiShortsPerformance: 5,
  /** Insight - AI 유사 Shorts 추천 */
  insightAiSimilarShorts: 5,
} as const;

// 요금제 키 ↔ planId 매핑
export const USER_PLAN_MAP = {
  free: 1,
  starter: 2,
  pro: 3,
  enterprise: 10,
  proPlus: 11,
  ultra: 12,
} as const;

export type UserPlanKey = keyof typeof USER_PLAN_MAP;
export type UserPlanId = (typeof USER_PLAN_MAP)[UserPlanKey];

export const USER_PLAN_DISPLAY_NAME: Record<UserPlanKey, string> = {
  free: 'Free',
  starter: 'Starter',
  pro: 'Pro',
  enterprise: 'Enterprise',
  proPlus: 'Pro+',
  ultra: 'Ultra',
} as const;
export type UserPlanDisplayName = (typeof USER_PLAN_DISPLAY_NAME)[UserPlanKey];

// DB 에만 존재하는 커스텀 계약(B2B/PoC) planId → 표준 플랜 키 별칭.
// 별칭된 planId 는 대상 플랜의 특성 전부(flags·limits·options·
// isProOrHigherPlan 류 우선순위 판정·워터마크 판정·플랜명 표시)를 상속한다.
// 단 USER_PLAN_MAP/USER_PLAN_PRIORITY 에는 키가 추가되지 않으므로
// 업그레이드 "추천 대상" 플랜 목록에 별칭 planId 가 노출되는 일은 없다.
const CUSTOM_PLAN_KEY_ALIAS: Record<number, UserPlanKey> = {
  15: 'enterprise', // 비상 PoC 커스텀 플랜 — enterprise 대우
};

// planId에서 역으로 키 조회
export const USER_PLAN_KEY_BY_ID: Record<number, UserPlanKey | undefined> = {
  ...Object.entries(USER_PLAN_MAP).reduce(
    (acc, [key, id]) => ({ ...acc, [id]: key }),
    {} as Record<UserPlanId, UserPlanKey>,
  ),
  ...CUSTOM_PLAN_KEY_ALIAS,
};

// 플랜 우선순위 (업/다운그레이드 판단 시 사용)
export const USER_PLAN_PRIORITY: UserPlanKey[] = [
  'free',
  'starter',
  'pro',
  'proPlus',
  'ultra',
  'enterprise',
] as const;

const USER_PLAN_PRIORITY_RANK = USER_PLAN_PRIORITY.reduce(
  (acc, key, index) => ({ ...acc, [key]: index }),
  {} as Record<UserPlanKey, number>,
);

// 기능 플래그: 한국어 명세 항목을 키로 매핑 (순서 유지)
export const PLAN_FLAG_KEYS = {
  // 원본 영상 sources
  ORIGINAL_VIDEO_SOURCES: 'originalVideoSources',
  // 업로드 방식
  UPLOAD_METHODS: 'uploadMethods',
  // 원본 영상 해상도
  ORIGINAL_VIDEO_RESOLUTION: 'originalVideoResolution',
  // LTS 원본 영상 용량
  LTS_ORIGINAL_VIDEO_SIZE: 'ltsOriginalVideoSize',
  // MTS 원본 영상 길이 및 용량
  MTS_ORIGINAL_VIDEO_DURATION_SIZE: 'mtsOriginalVideoDurationSize',
  // [AI] camera Tracking
  AI_CAMERA_TRACKING: 'aiCameraTracking',
  // 채팅으로 컷편집 (성능은 바꿀 수 있음)
  CHAT_CUT_EDITING: 'chatCutEditing',
  // 채팅으로 영상 검색
  CHAT_VIDEO_SEARCH: 'chatVideoSearch',
  // 검색으로 제작한 영상 내보내기
  EXPORT_SEARCHED_VIDEO: 'exportSearchedVideo',
  // TTS 선택
  TTS_SELECTION: 'ttsSelection',
  // [AI] multi-language caption
  AI_MULTI_LANGUAGE_CAPTION: 'aiMultiLanguageCaption',
  // 영상 비율
  ASPECT_RATIO: 'aspectRatio',
  // [AI] 자동 레이아웃 전환
  AI_AUTO_LAYOUT_SWITCHING: 'aiAutoLayoutSwitching',
  // [AI] Auto reframing
  AI_AUTO_REFRAINING: 'aiAutoRefraining',
  // editor 사용
  EDITOR_ACCESS: 'editorAccess',
  // 브랜드 템플릿
  BRAND_TEMPLATES: 'brandTemplates',
  // 브랜드 템플릿 추가하기
  ADD_BRAND_TEMPLATES: 'addBrandTemplates',
  // 폰트 사용
  FONT_USAGE: 'fontUsage',
  // 커스텀 폰트 추가
  ADD_CUSTOM_FONTS: 'addCustomFonts',
  // 클립(영상) 추가
  ADD_CLIPS: 'addClips',
  // Transcript 기반 편집
  TRANSCRIPT_BASED_EDITING: 'transcriptBasedEditing',
  // 제목 수정
  EDIT_TITLE: 'editTitle',
  // [AI] 제목 생성
  AI_GENERATE_TITLE: 'aiGenerateTitle',
  // 자막 수정
  SUBTITLE_EDITING: 'subtitleEditing',
  // 아이템 일괄 수정
  BULK_ITEM_EDITING: 'bulkItemEditing',
  // 이미지/오디오 에셋 추가
  ADD_IMAGE_AUDIO_ASSETS: 'addImageAudioAssets',
  // [AI] B-roll
  AI_B_ROLL: 'aiBRoll',
  // BGM
  BGM: 'bgm',
  // [AI] B-audio
  AI_B_AUDIO: 'aiBAudio',
  // 편집 효과/전환/필터
  EDITING_EFFECTS_TRANSITIONS_FILTERS: 'editingEffectsTransitionsFilters',
  // 워터마크 제거
  REMOVE_WATERMARK: 'removeWatermark',
  // 다운로드 화질
  DOWNLOAD_QUALITY: 'downloadQuality',
  // [AI] 업로드 정보 자동 작성
  AI_AUTO_FILL_UPLOAD_INFO: 'aiAutoFillUploadInfo',
  // 미리보기 링크 공유
  SHARE_PREVIEW_LINK: 'sharePreviewLink',
  // 영상 제작 리포트 + 공유
  PRODUCTION_REPORT_SHARING: 'productionReportSharing',
  // 소셜 업로드
  SOCIAL_UPLOAD: 'socialUpload',
  // 소셜 업로드 예약
  SCHEDULED_SOCIAL_UPLOAD: 'scheduledSocialUpload',
  // 내보내기 영상 보관 만료 기한
  EXPORTED_VIDEO_RETENTION: 'exportedVideoRetention',
  // 프로젝트 만료 기한
  PROJECT_EXPIRATION: 'projectExpiration',
  // 에셋 스토리지 제공
  ASSET_STORAGE_QUOTA: 'assetStorageQuota',
  // 공유 스페이스 (팀)
  SHARED_SPACE_TEAM: 'sharedSpaceTeam',
  // 프로젝트 복사 / 공유
  PROJECT_COPY_SHARE: 'projectCopyShare',
} as const;
// 플랜별 기능 토글 키 타입
export type PlanFlagKey = (typeof PLAN_FLAG_KEYS)[keyof typeof PLAN_FLAG_KEYS];

// 수치 제한 키
export const PLAN_LIMIT_KEYS = {
  ORIGINAL_VIDEO_RESOLUTION: 'originalVideoResolution',
  LTS_ORIGINAL_VIDEO_SIZE_GB: 'ltsOriginalVideoSizeGb',
  MTS_ORIGINAL_VIDEO_DURATION_MIN: 'mtsOriginalVideoDurationMin',
  MTS_ORIGINAL_VIDEO_SIZE_GB: 'mtsOriginalVideoSizeGb',
  DOWNLOAD_QUALITY_RESOLUTION: 'downloadQualityResolution',
  EXPORTED_VIDEO_RETENTION_DAYS: 'exportedVideoRetentionDays',
  PROJECT_EXPIRATION_DAYS: 'projectExpirationDays',
  ASSET_STORAGE_QUOTA_MB: 'assetStorageQuotaMb',
  HIGH_QUALITY_TTS_LEVEL: 'highQualityTtsLevel',
} as const;
export type PlanLimitKey =
  (typeof PLAN_LIMIT_KEYS)[keyof typeof PLAN_LIMIT_KEYS];

// 옵션/선택지 키
export const PLAN_OPTION_KEYS = {
  ORIGINAL_VIDEO_SOURCES: 'originalVideoSources',
  UPLOAD_METHODS: 'uploadMethods',
  ASPECT_RATIO: 'aspectRatio',
  BGM_ACCESS: 'bgmAccess',
} as const;
export type PlanOptionKey =
  (typeof PLAN_OPTION_KEYS)[keyof typeof PLAN_OPTION_KEYS];

// 기능 제공 상태
type FeatureAvailability = 'enabled' | 'disabled' | 'planned';
// 단일 제한 값 모델
type PlanLimitValue = {
  unit: 'minutes' | 'gb' | 'mb' | 'days' | 'count' | 'resolution';
  value: number | null;
  note?: string;
};
// 플랜별 전체 스펙
type PlanCapabilities = {
  flags: Partial<Record<PlanFlagKey, FeatureAvailability>>;
  limits: Partial<Record<PlanLimitKey, PlanLimitValue>>;
  options: Partial<Record<PlanOptionKey, readonly string[]>>;
};

// 업로드/옵션 공통 프리셋
const BASE_UPLOAD_SOURCES = ['youtube', 'file'] as const;
const EXTENDED_UPLOAD_SOURCES = [
  'youtube',
  'file',
  'tiktok',
  'gdrive',
  'cloud',
] as const;
const DEFAULT_ASPECT_RATIOS = ['9:16', '1:1', '16:9'] as const;

// 플랜별 기능/제한/옵션 스펙
const PLAN_CAPABILITIES: Record<UserPlanKey, PlanCapabilities> = {
  free: {
    flags: {
      originalVideoSources: 'enabled',
      uploadMethods: 'enabled',
      originalVideoResolution: 'enabled',
      ltsOriginalVideoSize: 'enabled',
      mtsOriginalVideoDurationSize: 'enabled',
      aiCameraTracking: 'disabled',
      chatCutEditing: 'planned',
      chatVideoSearch: 'planned',
      exportSearchedVideo: 'planned',
      ttsSelection: 'enabled',
      aiMultiLanguageCaption: 'disabled',
      aspectRatio: 'enabled',
      aiAutoLayoutSwitching: 'planned',
      aiAutoRefraining: 'disabled',
      editorAccess: 'enabled',
      brandTemplates: 'enabled',
      addBrandTemplates: 'disabled',
      fontUsage: 'disabled',
      addCustomFonts: 'disabled',
      addClips: 'disabled',
      transcriptBasedEditing: 'enabled',
      editTitle: 'enabled',
      aiGenerateTitle: 'disabled',
      subtitleEditing: 'enabled',
      bulkItemEditing: 'planned',
      addImageAudioAssets: 'disabled',
      aiBRoll: 'disabled',
      bgm: 'enabled',
      aiBAudio: 'disabled',
      editingEffectsTransitionsFilters: 'enabled',
      removeWatermark: 'planned',
      downloadQuality: 'enabled',
      aiAutoFillUploadInfo: 'planned',
      sharePreviewLink: 'enabled',
      productionReportSharing: 'disabled',
      socialUpload: 'enabled',
      scheduledSocialUpload: 'planned',
      exportedVideoRetention: 'enabled',
      projectExpiration: 'enabled',
      assetStorageQuota: 'enabled',
      sharedSpaceTeam: 'planned',
      projectCopyShare: 'enabled',
    },
    limits: {
      originalVideoResolution: { unit: 'resolution', value: 2160 },
      ltsOriginalVideoSizeGb: { unit: 'gb', value: 1 },
      mtsOriginalVideoDurationMin: { unit: 'minutes', value: 30 },
      mtsOriginalVideoSizeGb: { unit: 'gb', value: 1 },
      downloadQualityResolution: { unit: 'resolution', value: 720 },
      exportedVideoRetentionDays: { unit: 'days', value: 14 },
      projectExpirationDays: { unit: 'days', value: 3 },
      assetStorageQuotaMb: { unit: 'mb', value: 100 },
      highQualityTtsLevel: { unit: 'count', value: 1 },
    },
    options: {
      originalVideoSources: BASE_UPLOAD_SOURCES,
      uploadMethods: BASE_UPLOAD_SOURCES,
      aspectRatio: DEFAULT_ASPECT_RATIOS,
      bgmAccess: ['partial-free'],
    },
  },
  starter: {
    flags: {
      originalVideoSources: 'enabled',
      uploadMethods: 'enabled',
      originalVideoResolution: 'enabled',
      ltsOriginalVideoSize: 'enabled',
      mtsOriginalVideoDurationSize: 'enabled',
      aiCameraTracking: 'enabled',
      chatCutEditing: 'enabled',
      chatVideoSearch: 'enabled',
      exportSearchedVideo: 'enabled',
      ttsSelection: 'enabled',
      aiMultiLanguageCaption: 'disabled',
      aspectRatio: 'enabled',
      aiAutoLayoutSwitching: 'planned',
      aiAutoRefraining: 'enabled',
      editorAccess: 'enabled',
      brandTemplates: 'enabled',
      addBrandTemplates: 'disabled',
      fontUsage: 'enabled',
      addCustomFonts: 'enabled',
      addClips: 'enabled',
      transcriptBasedEditing: 'enabled',
      editTitle: 'enabled',
      aiGenerateTitle: 'enabled',
      subtitleEditing: 'enabled',
      bulkItemEditing: 'planned',
      addImageAudioAssets: 'planned',
      aiBRoll: 'planned',
      bgm: 'enabled',
      aiBAudio: 'disabled',
      editingEffectsTransitionsFilters: 'enabled',
      removeWatermark: 'planned',
      downloadQuality: 'enabled',
      aiAutoFillUploadInfo: 'planned',
      sharePreviewLink: 'enabled',
      productionReportSharing: 'enabled',
      socialUpload: 'enabled',
      scheduledSocialUpload: 'planned',
      exportedVideoRetention: 'enabled',
      projectExpiration: 'enabled',
      assetStorageQuota: 'enabled',
      sharedSpaceTeam: 'planned',
      projectCopyShare: 'enabled',
    },
    limits: {
      originalVideoResolution: { unit: 'resolution', value: 2160 },
      ltsOriginalVideoSizeGb: { unit: 'gb', value: 1 },
      mtsOriginalVideoDurationMin: { unit: 'minutes', value: 30 },
      mtsOriginalVideoSizeGb: { unit: 'gb', value: 2 },
      downloadQualityResolution: { unit: 'resolution', value: 1080 },
      exportedVideoRetentionDays: { unit: 'days', value: 30 },
      projectExpirationDays: { unit: 'days', value: 30 },
      assetStorageQuotaMb: { unit: 'mb', value: 300 },
    },
    options: {
      originalVideoSources: BASE_UPLOAD_SOURCES,
      uploadMethods: BASE_UPLOAD_SOURCES,
      aspectRatio: DEFAULT_ASPECT_RATIOS,
      bgmAccess: ['all'],
    },
  },
  pro: {
    flags: {
      originalVideoSources: 'enabled',
      uploadMethods: 'enabled',
      originalVideoResolution: 'enabled',
      ltsOriginalVideoSize: 'enabled',
      mtsOriginalVideoDurationSize: 'enabled',
      aiCameraTracking: 'enabled',
      chatCutEditing: 'enabled',
      chatVideoSearch: 'enabled',
      exportSearchedVideo: 'enabled',
      ttsSelection: 'enabled',
      aiMultiLanguageCaption: 'enabled',
      aspectRatio: 'enabled',
      aiAutoLayoutSwitching: 'planned',
      aiAutoRefraining: 'enabled',
      editorAccess: 'enabled',
      brandTemplates: 'enabled',
      addBrandTemplates: 'enabled',
      fontUsage: 'enabled',
      addCustomFonts: 'enabled',
      addClips: 'enabled',
      transcriptBasedEditing: 'enabled',
      editTitle: 'enabled',
      aiGenerateTitle: 'enabled',
      subtitleEditing: 'enabled',
      bulkItemEditing: 'planned',
      addImageAudioAssets: 'planned',
      aiBRoll: 'planned',
      bgm: 'enabled',
      aiBAudio: 'planned',
      editingEffectsTransitionsFilters: 'enabled',
      removeWatermark: 'planned',
      downloadQuality: 'enabled',
      aiAutoFillUploadInfo: 'planned',
      sharePreviewLink: 'enabled',
      productionReportSharing: 'enabled',
      socialUpload: 'enabled',
      scheduledSocialUpload: 'enabled',
      exportedVideoRetention: 'enabled',
      projectExpiration: 'enabled',
      assetStorageQuota: 'enabled',
      sharedSpaceTeam: 'planned',
      projectCopyShare: 'enabled',
    },
    limits: {
      originalVideoResolution: { unit: 'resolution', value: 2160 },
      ltsOriginalVideoSizeGb: { unit: 'gb', value: 2 },
      mtsOriginalVideoDurationMin: { unit: 'minutes', value: 30 },
      mtsOriginalVideoSizeGb: { unit: 'gb', value: 2 },
      downloadQualityResolution: { unit: 'resolution', value: 2160 },
      exportedVideoRetentionDays: { unit: 'days', value: 90 },
      projectExpirationDays: { unit: 'days', value: 30 },
      assetStorageQuotaMb: { unit: 'mb', value: 2048 },
    },
    options: {
      originalVideoSources: EXTENDED_UPLOAD_SOURCES,
      uploadMethods: EXTENDED_UPLOAD_SOURCES,
      aspectRatio: DEFAULT_ASPECT_RATIOS,
      bgmAccess: ['all'],
    },
  },
  proPlus: {
    flags: {
      originalVideoSources: 'enabled',
      uploadMethods: 'enabled',
      originalVideoResolution: 'enabled',
      ltsOriginalVideoSize: 'enabled',
      mtsOriginalVideoDurationSize: 'enabled',
      aiCameraTracking: 'enabled',
      chatCutEditing: 'enabled',
      chatVideoSearch: 'enabled',
      exportSearchedVideo: 'enabled',
      ttsSelection: 'enabled',
      aiMultiLanguageCaption: 'enabled',
      aspectRatio: 'enabled',
      aiAutoLayoutSwitching: 'planned',
      aiAutoRefraining: 'enabled',
      editorAccess: 'enabled',
      brandTemplates: 'enabled',
      addBrandTemplates: 'enabled',
      fontUsage: 'enabled',
      addCustomFonts: 'enabled',
      addClips: 'enabled',
      transcriptBasedEditing: 'enabled',
      editTitle: 'enabled',
      aiGenerateTitle: 'enabled',
      subtitleEditing: 'enabled',
      bulkItemEditing: 'planned',
      addImageAudioAssets: 'planned',
      aiBRoll: 'planned',
      bgm: 'enabled',
      aiBAudio: 'planned',
      editingEffectsTransitionsFilters: 'enabled',
      removeWatermark: 'planned',
      downloadQuality: 'enabled',
      aiAutoFillUploadInfo: 'planned',
      sharePreviewLink: 'enabled',
      productionReportSharing: 'enabled',
      socialUpload: 'enabled',
      scheduledSocialUpload: 'enabled',
      exportedVideoRetention: 'enabled',
      projectExpiration: 'enabled',
      assetStorageQuota: 'enabled',
      sharedSpaceTeam: 'planned',
      projectCopyShare: 'enabled',
    },
    limits: {
      originalVideoResolution: { unit: 'resolution', value: 2160 },
      ltsOriginalVideoSizeGb: { unit: 'gb', value: 2 },
      mtsOriginalVideoDurationMin: { unit: 'minutes', value: 30 },
      mtsOriginalVideoSizeGb: { unit: 'gb', value: 2 },
      downloadQualityResolution: { unit: 'resolution', value: 2160 },
      exportedVideoRetentionDays: { unit: 'days', value: 90 },
      projectExpirationDays: { unit: 'days', value: 30 },
      assetStorageQuotaMb: { unit: 'mb', value: 2048 },
    },
    options: {
      originalVideoSources: EXTENDED_UPLOAD_SOURCES,
      uploadMethods: EXTENDED_UPLOAD_SOURCES,
      aspectRatio: DEFAULT_ASPECT_RATIOS,
      bgmAccess: ['all'],
    },
  },
  ultra: {
    flags: {
      originalVideoSources: 'enabled',
      uploadMethods: 'enabled',
      originalVideoResolution: 'enabled',
      ltsOriginalVideoSize: 'enabled',
      mtsOriginalVideoDurationSize: 'enabled',
      aiCameraTracking: 'enabled',
      chatCutEditing: 'enabled',
      chatVideoSearch: 'enabled',
      exportSearchedVideo: 'enabled',
      ttsSelection: 'enabled',
      aiMultiLanguageCaption: 'enabled',
      aspectRatio: 'enabled',
      aiAutoLayoutSwitching: 'planned',
      aiAutoRefraining: 'enabled',
      editorAccess: 'enabled',
      brandTemplates: 'enabled',
      addBrandTemplates: 'enabled',
      fontUsage: 'enabled',
      addCustomFonts: 'enabled',
      addClips: 'enabled',
      transcriptBasedEditing: 'enabled',
      editTitle: 'enabled',
      aiGenerateTitle: 'enabled',
      subtitleEditing: 'enabled',
      bulkItemEditing: 'planned',
      addImageAudioAssets: 'planned',
      aiBRoll: 'planned',
      bgm: 'enabled',
      aiBAudio: 'planned',
      editingEffectsTransitionsFilters: 'enabled',
      removeWatermark: 'planned',
      downloadQuality: 'enabled',
      aiAutoFillUploadInfo: 'planned',
      sharePreviewLink: 'enabled',
      productionReportSharing: 'enabled',
      socialUpload: 'enabled',
      scheduledSocialUpload: 'enabled',
      exportedVideoRetention: 'enabled',
      projectExpiration: 'enabled',
      assetStorageQuota: 'enabled',
      sharedSpaceTeam: 'planned',
      projectCopyShare: 'enabled',
    },
    limits: {
      originalVideoResolution: { unit: 'resolution', value: 2160 },
      ltsOriginalVideoSizeGb: { unit: 'gb', value: 2 },
      mtsOriginalVideoDurationMin: { unit: 'minutes', value: 30 },
      mtsOriginalVideoSizeGb: { unit: 'gb', value: 2 },
      downloadQualityResolution: { unit: 'resolution', value: 2160 },
      exportedVideoRetentionDays: { unit: 'days', value: 90 },
      projectExpirationDays: { unit: 'days', value: 30 },
      assetStorageQuotaMb: { unit: 'mb', value: 2048 },
    },
    options: {
      originalVideoSources: EXTENDED_UPLOAD_SOURCES,
      uploadMethods: EXTENDED_UPLOAD_SOURCES,
      aspectRatio: DEFAULT_ASPECT_RATIOS,
      bgmAccess: ['all'],
    },
  },
  // Enterprise (planId=10): product_id=10 의 product_item 데이터 기준
  // - BE FeatureToggle/Numeric/Enum 매핑 항목은 DB 값을 그대로 반영
  // - FE-only flag(BE 매핑 없음)는 ultra 와 동일하게 처리
  enterprise: {
    flags: {
      originalVideoSources: 'enabled',
      uploadMethods: 'enabled',
      originalVideoResolution: 'enabled',
      ltsOriginalVideoSize: 'enabled',
      mtsOriginalVideoDurationSize: 'enabled',
      aiCameraTracking: 'enabled',
      chatCutEditing: 'enabled', // FeatureEnum 201 (default)
      chatVideoSearch: 'enabled', // FeatureToggle 201
      exportSearchedVideo: 'enabled', // FeatureToggle 202
      ttsSelection: 'enabled', // FeatureEnum 202
      aiMultiLanguageCaption: 'enabled', // FeatureToggle 301
      aspectRatio: 'enabled', // FeatureEnum 401
      aiAutoLayoutSwitching: 'planned',
      aiAutoRefraining: 'enabled',
      editorAccess: 'enabled', // FeatureToggle 501
      brandTemplates: 'enabled', // FeatureNumeric 501 (10000)
      addBrandTemplates: 'enabled',
      fontUsage: 'enabled',
      addCustomFonts: 'enabled',
      addClips: 'enabled', // FeatureToggle 503
      transcriptBasedEditing: 'enabled', // FeatureToggle 504
      editTitle: 'enabled', // FeatureToggle 505
      aiGenerateTitle: 'enabled', // FeatureToggle 506
      subtitleEditing: 'enabled', // FeatureToggle 507
      bulkItemEditing: 'planned',
      addImageAudioAssets: 'enabled', // FeatureToggle 508
      aiBRoll: 'planned',
      bgm: 'enabled',
      aiBAudio: 'planned',
      editingEffectsTransitionsFilters: 'enabled',
      removeWatermark: 'enabled', // FeatureToggle 601
      downloadQuality: 'enabled',
      aiAutoFillUploadInfo: 'planned',
      sharePreviewLink: 'enabled',
      productionReportSharing: 'enabled',
      socialUpload: 'enabled', // FeatureToggle 802
      scheduledSocialUpload: 'enabled',
      exportedVideoRetention: 'enabled',
      projectExpiration: 'enabled', // FeatureNumeric 702
      assetStorageQuota: 'enabled', // FeatureNumeric 703
      sharedSpaceTeam: 'planned',
      projectCopyShare: 'enabled',
    },
    limits: {
      originalVideoResolution: { unit: 'resolution', value: 2160 },
      ltsOriginalVideoSizeGb: { unit: 'gb', value: null, note: 'unlimited' }, // FeatureNumeric 101: MAX
      mtsOriginalVideoDurationMin: { unit: 'minutes', value: 360 }, // FeatureNumeric 104: 6시간
      mtsOriginalVideoSizeGb: { unit: 'gb', value: null, note: 'unlimited' }, // FeatureNumeric 103: MAX
      downloadQualityResolution: { unit: 'resolution', value: 2160 },
      exportedVideoRetentionDays: {
        unit: 'days',
        value: null,
        note: 'unlimited',
      },
      projectExpirationDays: { unit: 'days', value: 60 }, // FeatureNumeric 702
      assetStorageQuotaMb: { unit: 'mb', value: null, note: 'unlimited' }, // FeatureNumeric 703: MAX
    },
    options: {
      originalVideoSources: EXTENDED_UPLOAD_SOURCES,
      uploadMethods: EXTENDED_UPLOAD_SOURCES,
      aspectRatio: DEFAULT_ASPECT_RATIOS, // FeatureEnum 401: 9:16, 16:9, 1:1
      bgmAccess: ['all'],
    },
  },
};

/** planId(number)을 표준 키로 변환 */
const resolvePlanKey = (planId: number): UserPlanKey | undefined =>
  USER_PLAN_KEY_BY_ID[planId as UserPlanId];

// 기능의 현재 상태를 평가: planned를 포함할지(includePlanned) 선택적으로 허용
const isPlanFeatureAvailable = (
  planKey: UserPlanKey,
  feature: PlanFlagKey,
  includePlanned?: boolean,
) => {
  const availability = PLAN_CAPABILITIES[planKey].flags[feature];
  if (!availability || availability === 'disabled') return false;
  if (availability === 'planned') {
    return Boolean(includePlanned);
  }
  return true;
};

/** 플랜에서 특정 기능이 사용 가능한지 반환 */
export const hasPlanFeature = (
  planId: number,
  feature: PlanFlagKey,
  options?: { includePlanned?: boolean },
): boolean => {
  const planKey = resolvePlanKey(planId);
  if (!planKey) return false;

  return isPlanFeatureAvailable(planKey, feature, options?.includePlanned);
};

/** 워터마크가 필수로 노출되어야 하는 플랜인지 여부 */
export const isWatermarkRequiredOnPlan = (planId: number): boolean => {
  const planKey = resolvePlanKey(planId);
  if (!planKey) return false;

  // 현재 정책: free 플랜만 워터마크 고정 노출
  return planKey === 'free';
};

/** Starter 플랜 이상인지(Starter 포함) 여부를 확인 */
export const isStarterOrHigherPlan = (planId: number): boolean => {
  const planKey = resolvePlanKey(planId);
  if (!planKey) return false;

  const starterIndex = USER_PLAN_PRIORITY.indexOf('starter');
  const currentIndex = USER_PLAN_PRIORITY.indexOf(planKey);

  if (starterIndex === -1 || currentIndex === -1) {
    return false;
  }

  return currentIndex >= starterIndex;
};

/** Pro 플랜 이상인지(Pro 포함) 여부를 확인 */
export const isProOrHigherPlan = (planId: number): boolean => {
  const planKey = resolvePlanKey(planId);
  if (!planKey) return false;

  const proIndex = USER_PLAN_PRIORITY.indexOf('pro');
  const currentIndex = USER_PLAN_PRIORITY.indexOf(planKey);

  if (proIndex === -1 || currentIndex === -1) {
    return false;
  }

  return currentIndex >= proIndex;
};

/**
 * 기능 사용을 시작할 수 있는 최소 플랜 키 조회
 * - includePlanned=true면 출시 예정(planned) 플랜도 "이 플랜부터"로 안내
 * - includePlanned=false면 현재 사용 가능한(enabled) 플랜만 인정
 */
export const getMinimumPlanKeyForFeature = (
  feature: PlanFlagKey,
  options?: { includePlanned?: boolean },
): UserPlanKey | undefined => {
  const enabledPlanKey = USER_PLAN_PRIORITY.find((planKey) =>
    isPlanFeatureAvailable(planKey, feature, false),
  );
  if (enabledPlanKey) return enabledPlanKey;

  if (options?.includePlanned ?? true) {
    return USER_PLAN_PRIORITY.find(
      (planKey) => PLAN_CAPABILITIES[planKey].flags[feature] === 'planned',
    );
  }

  return undefined;
};

/**
 * 기능 사용이 가능해지는 최소 플랜의 planId와 표시 이름을 반환
 * - includePlanned=true면 출시 예정(planned) 플랜도 허용
 */
export const getMinimumPlanInfoForFeature = (
  feature: PlanFlagKey,
  options?: { includePlanned?: boolean },
):
  | {
      planId: UserPlanId;
      planName: UserPlanDisplayName;
    }
  | undefined => {
  const minimumPlanKey = getMinimumPlanKeyForFeature(feature, options);
  if (!minimumPlanKey) return undefined;

  return {
    planId: USER_PLAN_MAP[minimumPlanKey],
    planName: USER_PLAN_DISPLAY_NAME[minimumPlanKey],
  };
};

/**
 * planId와 기능 키를 받아 해당 기능을 사용할 수 있는 최소 플랜 ID 반환
 * - planId가 유효하지 않으면 undefined
 * - includePlanned=true면 출시 예정 플랜도 허용
 */
export const getMinimumPlanIdForFeature = (
  planId: number,
  feature: PlanFlagKey,
  options?: { includePlanned?: boolean },
): UserPlanId | undefined => {
  const planKey = resolvePlanKey(planId);
  if (!planKey) return undefined;

  const minimumPlanInfo = getMinimumPlanInfoForFeature(feature, options);
  if (!minimumPlanInfo) return undefined;

  return minimumPlanInfo.planId;
};

/** 플랜의 지정된 수치 제한 정보를 반환 */
export const getPlanLimit = (
  planId: number,
  limitKey: PlanLimitKey,
): PlanLimitValue | undefined => {
  const planKey = resolvePlanKey(planId);
  if (!planKey) return undefined;
  return PLAN_CAPABILITIES[planKey].limits[limitKey];
};

/** 고품질 TTS 사용이 차단되는 플랜인지 여부를 반환 (PLAN_LIMIT_KEYS 기반) */
export const isHighQualityTtsBlockedOnPlan = (planId: number): boolean => {
  const limit = getPlanLimit(planId, PLAN_LIMIT_KEYS.HIGH_QUALITY_TTS_LEVEL);
  if (!limit) return false;

  // value === 1 인 경우를 "차단"으로 정의
  return limit.value === 1;
};

/** 요청값이 플랜 제한 이내인지 확인 */
export const isWithinPlanLimit = (
  planId: number,
  limitKey: PlanLimitKey,
  requestedValue: number,
): boolean => {
  const limit = getPlanLimit(planId, limitKey);
  if (!limit) return false;
  if (limit.value === null) return true;
  return requestedValue <= limit.value;
};

/** 플랜에서 지원하는 옵션 리스트를 반환 */
export const getPlanOptions = (
  planId: number,
  optionKey: PlanOptionKey,
): readonly string[] => {
  const planKey = resolvePlanKey(planId);
  if (!planKey) return [];
  return PLAN_CAPABILITIES[planKey].options[optionKey] ?? [];
};

/** 기준 플랜보다 높은 등급인지 확인 */
export const isHigherPlanThan = (
  planId: number,
  basePlanId: number,
): boolean => {
  const planKey = resolvePlanKey(planId);
  const baseKey = resolvePlanKey(basePlanId);
  if (!planKey || !baseKey) return false;
  return USER_PLAN_PRIORITY_RANK[planKey] > USER_PLAN_PRIORITY_RANK[baseKey];
};
